import { useSnapshot } from 'valtio';
import { Play, CheckCircle2 } from 'lucide-react';
import { store, actions } from '@/store';
import { ONBOARDING_VIDEOS } from '@/lib/onboarding-videos';

export function OnboardingWidget() {
  const snap = useSnapshot(store);

  // Find the next unwatched video
  const nextVideo = ONBOARDING_VIDEOS.find(
    (video) => !snap.completedVideoIds.includes(video.id)
  );

  // Calculate progress
  const completedCount = snap.completedVideoIds.length;
  const totalCount = ONBOARDING_VIDEOS.length;
  const allCompleted = completedCount === totalCount;

  const handleOpenOnboarding = () => {
    actions.setShowOnboarding(true);
  };

  // Don't show widget if user has never seen onboarding (they'll see the modal)
  if (!snap.onboardingCompleted && snap.showOnboarding) {
    return null;
  }

  return (
    <button
      onClick={handleOpenOnboarding}
      className="w-full p-3 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border border-primary/20 rounded-lg hover:border-primary/40 transition-all group"
    >
      <div className="space-y-3">
        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-foreground mb-0.5">
              {allCompleted ? 'Review Training' : 'Continue Training'}
            </h3>
            <p className="text-xs text-muted-foreground">
              {allCompleted
                ? 'Rewatch any tutorial'
                : `${completedCount} of ${totalCount} completed`}
            </p>
          </div>
          {allCompleted ? (
            <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
          ) : (
            <div className="h-5 w-5 rounded-full border-2 border-primary flex items-center justify-center flex-shrink-0">
              <div className="h-2 w-2 rounded-full bg-primary" />
            </div>
          )}
        </div>

        {/* Progress bar */}
        {!allCompleted && (
          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary transition-all duration-300"
              style={{ width: `${(completedCount / totalCount) * 100}%` }}
            />
          </div>
        )}

        {/* Next video preview */}
        {nextVideo && !allCompleted && (
          <div className="bg-black/5 dark:bg-white/5 rounded-md p-3 flex items-center gap-3 group-hover:bg-black/10 dark:group-hover:bg-white/10 transition-colors">
            <div className="relative flex-shrink-0">
              {/* Video thumbnail placeholder */}
              <div className="w-16 h-10 bg-gradient-to-br from-gray-700 to-gray-900 rounded flex items-center justify-center">
                <div className="w-6 h-6 rounded-full bg-white/90 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Play className="h-3 w-3 text-gray-900 fill-gray-900 ml-0.5" />
                </div>
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-foreground truncate">
                {nextVideo.title}
              </p>
              <p className="text-xs text-muted-foreground">
                {nextVideo.duration}
              </p>
            </div>
          </div>
        )}

        {/* CTA for completed state */}
        {allCompleted && (
          <div className="flex items-center justify-center gap-2 text-xs font-medium text-primary">
            <Play className="h-3 w-3" />
            <span>Rewatch tutorials</span>
          </div>
        )}
      </div>
    </button>
  );
}
